/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

// eslint-disable-next-line no-restricted-imports
import type {
  ContentStackDefaultPdpPageBody,
  ContentStackContent,
} from '@/features/contentstack/interface/content-types';
import type {
  ConvertedAnchor,
  ConvertedMatchedAnchors,
} from '../lib/productDetailsPage/getEnhancedContent/getEnhancedContent';

/**
 * Stitches enhanced PDP anchor content into the default PDP page body.
 *
 * Modular action rules (per anchor):
 *   'above'          → enhanced content prepended BEFORE default anchor content
 *   'below'          → enhanced content prepended AFTER default anchor content
 *   'replace'        → enhanced content completely REPLACES default anchor content
 */
export function stitchEnhancedPDPContent(
  defaultPageBody: ContentStackDefaultPdpPageBody[],
  enhancedAnchors: ConvertedMatchedAnchors,
): ContentStackDefaultPdpPageBody[] {
  return defaultPageBody.map((block) => {
    if ('anchor_top' in block && block.anchor_top && enhancedAnchors.top) {
      return {
        ...block,
        anchor_top: {
          ...block.anchor_top,
          content: mergeContent(block.anchor_top.content, enhancedAnchors.top),
        },
      };
    }
    if ('anchor_middle' in block && block.anchor_middle && enhancedAnchors.middle) {
      return {
        ...block,
        anchor_middle: {
          ...block.anchor_middle,
          content: mergeContent(block.anchor_middle.content, enhancedAnchors.middle),
        },
      };
    }
    if ('anchor_bottom' in block && block.anchor_bottom && enhancedAnchors.bottom) {
      return {
        ...block,
        anchor_bottom: {
          ...block.anchor_bottom,
          content: mergeContent(block.anchor_bottom.content, enhancedAnchors.bottom),
        },
      };
    }
    return block;
  });
}

export function mergeContent(
  defaultContent: ContentStackContent[] | undefined,
  enhanced: ConvertedAnchor,
): ContentStackContent[] {
  const action = enhanced.modular_action?.toLowerCase();

  if (action?.includes('replace')) {
    return [enhanced.content as ContentStackContent];
  }
  if (action?.includes('above')) {
    return [enhanced.content as ContentStackContent, ...(defaultContent ?? [])];
  }
  if (action?.includes('below')) {
    return [...(defaultContent ?? []), enhanced.content as ContentStackContent];
  }
  // null or undefined — no modular action, return default content unchanged
  return defaultContent ?? [];
}
